import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../messages/en-US";
import { Button } from "@progress/kendo-react-buttons";
import { Form, Field, FormElement} from "@progress/kendo-react-form";
import { FormDropDownList, FormInput, FormMultiEmail, FormSwitch, FormTextArea} from "../../Shared/Components/Forms/FormsComponents";
import { isEmail } from "react-multi-email";
import { useState } from "react";
import { DropDownListChangeEvent} from "@progress/kendo-react-dropdowns";
import { AlertUpdateDto } from "../../../Models/Alerts/AlertUpdateDto";
import { AlertCreateDto } from "../../../Models/Alerts/AlertCreateDto";
import { useToast } from "@chakra-ui/react";
import { requiredValidator } from "../Utilities/validators";
import { dataField, metricOperators, valueRenderCriticality } from "../Utilities/AddEditFormUtils";
import { ReservoirConditionComponent } from "./ReservoirCondition";
import { RequirementConditionComponent } from "./RequirementCondition";
import { PressureConditionComponent } from "./PressureCondition";
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import alertsApiService from "../Utilities/AlertsApiService";
import { CommsConditionComponent } from "./CommsCondition";
import { GreaseConditionComponent } from "./GreaseCondition";

interface Props {
  alert: any;
  onClose: () => void;
  isAddModal: boolean;
  alertId?: string;
  updateAlert?: (alertId: string, alert: AlertUpdateDto) => void;
  createAlert?: (alert: AlertCreateDto) => void;
}

export const AddEditAlertForm = (props: Props) => {
  const localizationService = useLocalization();
  const toast = useToast({
    position: "bottom",
    duration: 5000,
    isClosable: true,
  });

  const [showNotification, setShowNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [emails, setEmails] = useState<string[]>(props.alert.emails);
  const emailRequiredValidator = (value: string) =>
  {
    return emails.length > 0 ? "" : "Required";
  } 

  const dataType = [
    { 
        typeName: localizationService.toLanguageString("custom.reservoir", enMessages.custom.reservoir), 
        typeId: 1,
        value: "Reservoir"
    }, 
    { 
        typeName: localizationService.toLanguageString("custom.requirement", enMessages.custom.requirement), 
        typeId: 2,
        value: "Requirement"
    }, 
    { 
        typeName: localizationService.toLanguageString("custom.pressure", enMessages.custom.pressure), 
        typeId: 3,
        value: "Pressure"
    }, 
    { 
        typeName: localizationService.toLanguageString("custom.communications", enMessages.custom.communications), 
        typeId: 4,
        value: "Communication"
    },
    { 
      typeName: localizationService.toLanguageString("custom.grease", enMessages.custom.grease), 
      typeId: 5,
      value: "Grease"
  }
];
  const [selectedType, setSelectedType] = useState<number>()

  const typeChange = (event: DropDownListChangeEvent) => {
    const type = event.target.value;
    setSelectedType(type.typeId)
  };
  const handleCancel = (event, formRenderProps) => {
    event.preventDefault();
    formRenderProps.onFormReset();
    props.onClose();
  };

  const criticalityData = [
    {
        typeName: localizationService.toLanguageString("custom.low", enMessages.custom.low),
        value: "Low"
    },
    {
        typeName: localizationService.toLanguageString("custom.medium", enMessages.custom.medium),
        value: "Medium"
    },
    {
        typeName: localizationService.toLanguageString("custom.high", enMessages.custom.high),
        value: "High"
    },
    {
        typeName: localizationService.toLanguageString("custom.critical", enMessages.custom.critical),
        value: "Critical"
    }
];

  const handleSubmit = async (dataItem) => {
    setIsSubmitting(true);
    try {
      if (!props.isAddModal && props.updateAlert && props.alertId) {
        const castValues = {
          name: dataItem.name.trim(),
          description: dataItem.description,
          enabled: dataItem.enabled,
          conditions: dataItem.condition.conditions,
          message: dataItem.message,
          emails: emails,
          assignedTo: dataItem.assignedTo,
          criticality: dataItem.criticality.value,
          type: props.alert.type,
        } as AlertUpdateDto;

        await props.updateAlert(props.alertId, castValues);
        toast({
          status: "success",
          description: `'${
            dataItem.name
          }' ${localizationService.toLanguageString("custom.successfullyUpdated", enMessages.custom.successfullyUpdated
          )}`,
        });
      } else if (props.isAddModal && props.createAlert) {
        const castValues = {
          name: dataItem.name,
          description: dataItem.description,
          enabled: dataItem.enabled,
          conditions: dataItem.condition.conditions,
          message: dataItem.message,
          emails: emails,
          assignedTo: dataItem.condition.assignedTo,
          criticality: dataItem.criticality.value,
          type: dataItem.type.value,
        } as AlertCreateDto;

        await props.createAlert(castValues);

        toast({
          status: "success",
          description: `'${
            dataItem.name
          }'  ${localizationService.toLanguageString("custom.successfullyCreated", enMessages.custom.successfullyCreated
          )}`,
        });
      }
    } catch (e) {
      console.log(e)
      toast({
        title: `${localizationService.toLanguageString("custom.error", enMessages.custom.error
        )}`,
        status: "error",
        description: `${localizationService.toLanguageString("custom.somethingWentWrong", enMessages.custom.somethingWentWrong
        )}`,
      });
    } finally {
      setIsSubmitting(false);
    }

    props.onClose();
  };

  const handleTestEmail = async (e, formRenderProps) => {
    e.preventDefault();
    try {
      const alertData = {
        name: formRenderProps.valueGetter('name'),
        description: formRenderProps.valueGetter('description'),
        enabled: formRenderProps.valueGetter('enabled'),
        conditions: formRenderProps.valueGetter('condition').conditions,
        message: formRenderProps.valueGetter('message'),
        emails: emails,
        assignedTo: props.isAddModal ? formRenderProps.valueGetter('condition').assignedTo : [formRenderProps.valueGetter('condition').assignedTo],
        criticality: formRenderProps.valueGetter('criticality').value,
        type: props.isAddModal ? formRenderProps.valueGetter('type').value : props.alert.type,
      };
     
      const response = await alertsApiService.createTestEmail(alertData);
      
      if (response && response.status === 204) {
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);
      } else {
        console.log("Email failed to send", response);
        setShowErrorNotification(true);
        setTimeout(() => setShowErrorNotification(false), 3000);
      }
    } catch (e) {
      console.error("Error sending test email:", e);
      setShowErrorNotification(true);
      setTimeout(() => setShowErrorNotification(false), 3000);
    }
  };

  // Find the criticality object from criticalityData based on the string value
  const getCriticalityObject = (criticalityValue: string) => {
    if (!criticalityValue) return undefined;
    return criticalityData.find(item => item.value === criticalityValue);
  };

  return (
    <Form
      key={props.alert.name}
      initialValues={{
        name: props.alert.name,
        description: props.alert.description,
        enabled: props.alert.enabled,
        type: props.alert.type,
        criticality: getCriticalityObject(props.alert.criticality),
        fieldCondition:
          props.alert.conditions[0].field === undefined ? dataField.find((item) => item.fieldName === JSON.parse(props.alert.conditions[0]).field) : dataField.find((item) => item.fieldName === props.alert.conditions[0].field),
        operator:
          props.alert.conditions[0].operator === undefined
            ? metricOperators.find(
                (item) =>
                  item.value === JSON.parse(props.alert.conditions[0]).operator
              )
            : metricOperators.find(
                (item) => item.value === props.alert.conditions[0].operator
              ),
        value:
          props.alert.conditions[0].value === undefined
            ? JSON.parse(props.alert.conditions[0]).value
            : props.alert.conditions[0].value,
        unit:
          props.alert.conditions[0].unit === undefined
            ? { unitName: JSON.parse(props.alert.conditions[0]).unit }
            : { unitName: props.alert.conditions[0].unit },
        emails: props.alert.emails,
        message: props.alert.message,
        assignedTo: props.alert.assignedTo,
      }}
      onSubmit={handleSubmit}
      render={(formRenderProps) => (
        <FormElement className="h-full">
          <div className="flex flex-col justify-between h-full">
            <fieldset
              className={"k-form-fieldset"}
              style={{ padding: "0 10px 5px 5px", margin: "0px" }}
          >
            <div>
              <Field
                label={localizationService.toLanguageString("custom.name", enMessages.custom.name)}
                name={"name"}
                type={"text"}
                component={FormInput}
                style={{ height: 26 }}
                validator={requiredValidator}
                
              />
            </div>
            <div className="mt-3">
              <Field
                label={localizationService.toLanguageString(
                  "custom.description",
                  enMessages.custom.description
                )}
                name={"description"}
                type={"text"}
                component={FormTextArea}
                style={{ height: 28 }}
                required={false}
              />
            </div>
            <div className="flex justify-between items-baseline">
              <div className="">
                <Field
                  label={localizationService.toLanguageString("custom.enabled", enMessages.custom.enabled)}
                  name={"enabled"}
                  type={"text"}
                  component={FormSwitch}
                  trackRounded={"medium"}
                  thumbRounded={"medium"}
                  size={"small"}
                  defaultChecked={props.alert.enabled}
                />
              </div>
              <div className="" >
                {props.isAddModal ? (
                    <Field
                      label={localizationService.toLanguageString("custom.type",enMessages.custom.type)}
                      name="type"
                      component={FormDropDownList}
                      data={dataType}
                      textField="typeName"
                      dataItemKey="value"
                      validator={requiredValidator}
                      size={"small"}
                      onChange={typeChange}
                      style={{ width: "250px" }}
                    />
                ) : (
                  <div>
                    <label>{localizationService.toLanguageString("custom.type",enMessages.custom.type)}</label>
                    <div>{props.alert.type}</div>
                  </div>
                )}
              </div>
              <div className="" style={{ width: "208px" }}>
                  <Field
                    label={localizationService.toLanguageString("custom.criticality",enMessages.custom.criticality)}
                    name={"criticality"}
                    component={FormDropDownList}
                    data={criticalityData}
                    textField="typeName"
                    dataItemKey="value"
                    validator={requiredValidator}
                    valueRender={valueRenderCriticality}
                    size={"small"}
                  />
              </div>
            </div>

            <div className="gb-h4" style={{ borderBottom: "1px solid #EBEBEB" }}>
              {localizationService.toLanguageString("custom.condition",enMessages.custom.condition)}
            </div>

             {
              (selectedType === 2 || props.alert.type === "Requirement") ?(
        
                <Field
                  key={'RequirementCondition'}
                  name={'condition'}
                  component={RequirementConditionComponent}
                  condition={{assignedTo: props.alert.assignedTo, conditions: props.alert.conditions}}
                  isAddModal={props.isAddModal}>
                </Field>
              ) : 
              (selectedType === 1 || props.alert.type === "Reservoir")  ?(
                <Field
                  key={'ReservoirCondition'}
                  name={'condition'}
                  component={ReservoirConditionComponent}
                  condition={{assignedTo: props.alert.assignedTo, conditions: props.alert.conditions}}
                  isAddModal={props.isAddModal}>
                </Field>
              ) : (selectedType === 3 || props.alert.type === "Pressure")  ?(
                <Field
                  key={'PressureCondition'}
                  name={'condition'}
                  component={PressureConditionComponent}
                  condition={{assignedTo: props.alert.assignedTo, conditions: props.alert.conditions}}
                  isAddModal={props.isAddModal}>
                </Field>
                ) :
              (selectedType === 4 || props.alert.type === "Communication") ? (
                <Field
                  key={'CommunicationsCondition'}
                  name={'condition'}
                  component={CommsConditionComponent}
                  condition={{assignedTo: props.alert.assignedTo, conditions: props.alert.conditions}}
                  isAddModal={props.isAddModal}>
                </Field>
              ) : 
              (selectedType === 5 || props.alert.type === "Grease") ? (
                <Field
                  key={'GreaseCondition'}
                  name={'condition'}
                  component={GreaseConditionComponent}
                  condition={{assignedTo: props.alert.assignedTo, conditions: props.alert.conditions}}
                  isAddModal={props.isAddModal}>
                </Field>
              ) :
              (
                <div> {localizationService.toLanguageString("custom.selectType",enMessages.custom.selectType)}...</div>
              )
            } 

            <div className="mt-4">
              <div
                className="gb-h4 mb-3"
                style={{ borderBottom: "1px solid #EBEBEB" }}
              >
                {localizationService.toLanguageString("custom.notifications",enMessages.custom.notifications)}
              </div>
              <Field
                name={"emails"}
                label={localizationService.toLanguageString("custom.notifyUsers", enMessages.custom.notifyUsers)}
                component={FormMultiEmail}
                validator={emailRequiredValidator}
                placeholder={localizationService.toLanguageString("custom.emails", enMessages.custom.emails)}
                emails={emails}
                onChange={(_to: string[]) => {
                  setEmails(_to);
                }}
                validateEmail={(email) => {
                  return isEmail(email);
                }}
                getLabel={(email, index, removeEmail) => {
                  return (
                    <div data-tag key={index}>
                      {email}
                      <span data-tag-handle onClick={() => removeEmail(index)}>
                        ×
                      </span>
                    </div>
                  );
                }}
              />

              <div className="mt-3">
                <Field
                  label={localizationService.toLanguageString("custom.message", enMessages.custom.message)}
                  name={"message"}
                  type={"text"}
                  component={FormTextArea}
                  rows={2}
                  required={false}
                />
              </div>
            </div>

            </fieldset>
            <div>
              <span className={"k-form-separator"} />
              <div className="flex justify-between k-form-button">
                <div className="p-2">
                  <Button
                    themeColor={"primary"}
                    onClick={(e) => handleTestEmail(e, formRenderProps)}
                    disabled={props.isAddModal ? (!formRenderProps.valid || !formRenderProps.touched || emails.length === 0) : emails.length === 0}
                  >
                    {localizationService.toLanguageString("custom.sendTestEmail", enMessages.custom.sendTestEmail)}
                  </Button>
                </div>
                <div className="p-2">
                  <Button
                    type={"submit"}
                    style={{
                      background: "#35AC00",
                      color: "white",
                      width: " 62px",
                      marginRight: "5px",
                    }}
                    disabled={!formRenderProps.allowSubmit || isSubmitting}
                  >
                    {localizationService.toLanguageString("custom.save", enMessages.custom.save)}
                  </Button>
                  <Button 
                    type="button"
                    onClick={(e) => handleCancel(e, formRenderProps)}
                  >
                    {localizationService.toLanguageString("custom.cancel", enMessages.custom.cancel)}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {showNotification && (
            <NotificationGroup
              style={{
                position: "absolute",
                bottom: 10,
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Fade>
                <Notification
                  type={{ style: 'success', icon: true }}
                  closable={true}
                  onClose={() => setShowNotification(false)}
                >
                  <span>{localizationService.toLanguageString("custom.testEmailSentSuccessfully", enMessages.custom.testEmailSentSuccessfully)}</span>
                </Notification>
              </Fade>
            </NotificationGroup>
          )}
          {showErrorNotification && (
            <NotificationGroup
              style={{
                position: "absolute",
                bottom: 10,
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Fade>
                <Notification
                  type={{ style: 'error', icon: true }}
                  closable={true}
                  onClose={() => setShowErrorNotification(false)}
                >
                  <span>{localizationService.toLanguageString("custom.testEmailFailedToSend", enMessages.custom.testEmailFailedToSend)}</span>
                </Notification>
              </Fade>
            </NotificationGroup>
          )}
        </FormElement>
      )}
    />
  );
};